import { formatPersonName } from "@/lib/formatPersonName";
import type { CommercialDigestPayload, FeedEntityScope } from "@/types/feed";

import {
  feedPartyMatchesViewer,
  hasFeedViewerPartyLink,
  liveFeedViewerParty,
  type FeedViewerPartyContext,
} from "./feedViewerParty";

function isCommercialDigestPayload(
  payload: unknown,
): payload is CommercialDigestPayload {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  return p.kind === "commercial_digest" && Array.isArray(p.bookings);
}

function opportunityNoun(count: number, titleCase: boolean): string {
  if (count === 1) return titleCase ? "Opportunity" : "opportunity";
  return titleCase ? "Opportunities" : "opportunities";
}

function digestOwnerDimension(payload: CommercialDigestPayload): string | null {
  const dim = payload.scope?.dimension;
  if (dim === "sales_person" || dim === "opened_by" || dim === "agent") return dim;
  if (dim === "operator") return "opened_by";
  return null;
}

function digestOwnerRaw(
  payload: CommercialDigestPayload,
  scope?: FeedEntityScope | null,
): string | null {
  const dim = digestOwnerDimension(payload);
  const fromScope =
    dim === "sales_person"
      ? scope?.sales_person
      : dim === "opened_by"
        ? scope?.opened_by
        : dim === "agent"
          ? scope?.agent
          : (scope?.sales_person ?? scope?.opened_by ?? scope?.agent);
  return fromScope?.trim() || payload.scope?.key?.trim() || null;
}

function digestOwnerName(
  payload: CommercialDigestPayload,
  scope?: FeedEntityScope | null,
  viewer?: FeedViewerPartyContext | null,
): string | null {
  const raw = digestOwnerRaw(payload, scope);
  if (!raw) return null;
  const dim = digestOwnerDimension(payload) ?? "opened_by";
  const effective = viewer ?? liveFeedViewerParty();
  if (
    effective &&
    hasFeedViewerPartyLink(effective) &&
    feedPartyMatchesViewer(dim, raw, effective)
  ) {
    return null;
  }
  const formatted = formatPersonName(raw);
  return formatted === "—" ? null : formatted;
}

function digestOwnerBit(
  payload: CommercialDigestPayload,
  scope?: FeedEntityScope | null,
  viewer?: FeedViewerPartyContext | null,
): string {
  const raw = digestOwnerRaw(payload, scope);
  const dim = digestOwnerDimension(payload) ?? "opened_by";
  const effective = viewer ?? liveFeedViewerParty();
  if (
    raw &&
    effective &&
    hasFeedViewerPartyLink(effective) &&
    feedPartyMatchesViewer(dim, raw, effective)
  ) {
    return " for you";
  }
  const owner = digestOwnerName(payload, scope, viewer);
  return owner ? ` for ${owner}` : "";
}

/** Announcement title for a commercial digest — used for new and stored cards. */
export function commercialDigestDisplayTitle(
  payload: CommercialDigestPayload,
): string {
  const count = payload.booking_count;
  const noun = opportunityNoun(count, true);
  if (payload.dimension === "first_book") {
    return `Found ${count} First Booking ${noun}`;
  }
  if (payload.dimension === "rebook_opportunity") {
    return `Found ${count} Rebook ${noun}`;
  }
  return `Found ${count} ${noun}`;
}

/** Friendly summary — owner name only, no "no contract" / handling jargon. */
export function commercialDigestDisplaySummary(
  payload: CommercialDigestPayload,
  scope?: FeedEntityScope | null,
  viewer?: FeedViewerPartyContext | null,
): string {
  const count = payload.booking_count;
  const noun = opportunityNoun(count, false);
  const ownerBit = digestOwnerBit(payload, scope, viewer);
  if (payload.dimension === "first_book") {
    return `Found ${count} first booking ${noun}${ownerBit}.`;
  }
  if (payload.dimension === "rebook_opportunity") {
    const savings =
      payload.total_savings_usd != null && payload.total_savings_usd > 0
        ? ` · $${Math.round(payload.total_savings_usd)} potential savings`
        : "";
    return `Found ${count} rebook ${noun}${ownerBit}${savings}.`;
  }
  return `Found ${count} commercial ${noun}${ownerBit}.`;
}

export function commercialDigestCopyFromEvent(event: {
  payload?: unknown;
  entity_scope?: FeedEntityScope | null;
}): { title: string; summary: string } | null {
  if (!isCommercialDigestPayload(event.payload)) return null;
  return {
    title: commercialDigestDisplayTitle(event.payload),
    summary: commercialDigestDisplaySummary(
      event.payload,
      event.entity_scope,
    ),
  };
}
